import type { DbExecutor } from "./executor";
import type { ProjectRepository } from "./repositories/projectRepository";
import type { AcquisitionRepository } from "./repositories/acquisitionRepository";
import type { CategoryRepository } from "./repositories/categoryRepository";
import type { MileageRepository } from "./repositories/mileageRepository";
import type { ReconciliationService } from "./reconciliationService";
import type { BaselineService } from "./baselineService";
import type { ProcurementService } from "./procurementService";
import { ValidationError } from "./repositories/projectRepository";
import {
  BUCKET_ORDER,
  COMMITTED_COST_SEAM,
  projectCost,
  projectRehab,
  rehabControl,
  returnMetrics,
  rollup,
  saleResult,
  type ProjectCostBreakdown,
  type ProjectedRehab,
  type RehabControl,
  type ReturnMetrics,
  type RollupRow,
  type SaleResult,
  type UnlinkedProposedLine,
} from "../engine/projectFinancials";
import { actualBucketTotals, acquisitionExpensesCents, expenseTotalCents } from "../engine/financials";
import { validateSaleInput, type CloseoutWarning, type ProjectSale, type ProjectSaleInput } from "../domain/sale";
import type { FinancialBucket } from "../domain/buckets";
import type { Cents } from "../domain/money";
import type { ActualCostEvent } from "../engine/actualEvents";

/**
 * PROJECT FINANCIAL CONTROL & CLOSEOUT (Phase 12, spec §41A/§41B).
 *
 * Assembles the inputs the pure engine needs from the authoritative
 * records — baselines and change orders (planning), the normalized
 * ActualCostEvent stream and Phase 9C reconciliation (actual), acquisition
 * lines, mileage and the sale record — and returns ONE financial truth that
 * every view renders. It stores no totals: there is no "final actual cost"
 * column anywhere, by permanent rule.
 */

export interface ProjectFinancialDashboard {
  projectId: string;
  jobNumber: string;
  projectName: string;
  projectType: "renovation" | "new_construction";
  statusId: string;
  purchaseDate: string | null;

  purchasePriceCents: Cents;
  acquisitionCostsCents: Cents;
  bucketTotals: Record<FinancialBucket, Cents>;
  mileageCents: Cents;

  rehab: RehabControl;
  projection: ProjectedRehab;
  cost: ProjectCostBreakdown;

  /** Present once a sale record exists; otherwise the forecast is used. */
  sale: (SaleResult & { saleDate: string | null; closedAt: string | null }) | null;
  /**
   * Spec §73.1H: financing COSTS (interest + fees) in the projection —
   * recorded actuals plus the loan ledger's future payments and fees.
   * Principal is a funding source and appears in NO cost figure.
   */
  financingOutlook: {
    actualToDateCents: Cents;
    projectedFutureCents: Cents;
    totalProjectedCents: Cents;
  };
  expectedArvCents: Cents | null;
  baselineExpectedArvCents: Cents | null;
  /**
   * Spec §74S: what the category planning bases (selected quotes and
   * authorized amounts) changed against the calculated projection —
   * already included in rehab/cost figures, surfaced for display.
   */
  renovationBasisDeltaCents: Cents;
  /**
   * Spec §74Y: the project's investment strategy, so financial vocabulary
   * matches it — a rental's estimated value is never called profit.
   */
  strategy: "flip" | "rental";
  /** Forecast before sale, actual after it. */
  outcome: ReturnMetrics;
  /** Committed cost is deliberately not computed — the reason, in words. */
  committedCostSeam: string;
  warnings: CloseoutWarning[];
}

export class ProjectFinancialsService {
  constructor(
    private readonly db: DbExecutor,
    private readonly projects: ProjectRepository,
    private readonly acquisition: AcquisitionRepository,
    private readonly categories: CategoryRepository,
    private readonly mileage: MileageRepository,
    private readonly reconciliation: ReconciliationService,
    private readonly baselines: BaselineService,
    private readonly procurement: ProcurementService,
  ) {}

  /**
   * Spec §73.1H: the capital-plan service supplies FUTURE financing cost
   * (interest + fees the loan ledger still expects after today) so the
   * project's cost breakdown and profit forecast include the loan.
   * Injected after construction because the capital plan itself reads this
   * service's sale record (no cycle: it never calls dashboard()).
   */
  private financingProjector: ((projectId: string) => Promise<Cents>) | null = null;

  setFinancingProjector(projector: (projectId: string) => Promise<Cents>): void {
    this.financingProjector = projector;
  }

  /**
   * Spec §74S: the CATEGORY BUDGET's basis delta — what selected quotes
   * and authorized category amounts change against the calculated
   * estimate, over open categories. The per-feature Phase 9C projection
   * plus exactly this delta is the current renovation planning basis, so
   * a selected quote moves the projection ONCE and rewrites nothing.
   */
  private renovationBasisDelta: ((projectId: string, projection: ProjectedRehab) => Promise<Cents>) | null = null;

  setRenovationBasisDelta(provider: (projectId: string, projection: ProjectedRehab) => Promise<Cents>): void {
    this.renovationBasisDelta = provider;
  }

  private async strategyOf(projectId: string): Promise<"flip" | "rental"> {
    const rows = await this.db.select<{ strategy: string }>("SELECT strategy FROM projects WHERE id = ?", [projectId]);
    return rows[0]?.strategy === "rental" ? "rental" : "flip";
  }

  /* ------------------------------------------------------------------ */
  /* The dashboard                                                       */
  /* ------------------------------------------------------------------ */

  async dashboard(projectId: string): Promise<ProjectFinancialDashboard> {
    const project = await this.projects.getProject(projectId);
    if (!project) throw new ValidationError(["That project no longer exists."]);
    const [reconciliationResult, budget, acquisitionLines, categories, events, sale, mileageRows] = await Promise.all([
      this.reconciliation.forProject(projectId),
      this.baselines.budget(projectId),
      this.acquisition.listByProject(projectId),
      this.categories.listAll(),
      this.reconciliation.eventsForProject(projectId),
      this.getSale(projectId),
      this.mileage.listByProject(projectId),
    ]);

    const live = events.filter((e) => e.voidedAt === null);
    const buckets = actualBucketTotals(live, categories);
    const projection = projectRehab(reconciliationResult, await this.unlinkedScope(projectId, live, reconciliationResult));
    // Spec §74S: what the CATEGORY planning bases (selected quotes,
    // authorized amounts) change against the calculated estimate. Zero
    // when neither exists — bit-identical to the pre-§74 projection.
    const renovationBasisDeltaCents = this.renovationBasisDelta ? await this.renovationBasisDelta(projectId, projection) : 0;
    const projectedRehabCents = projection.projectedCents + renovationBasisDeltaCents;

    // Contingency-funded change orders draw on the frozen contingency
    // instead of raising the authorized budget (spec §27).
    const contingencyFunded = budget.changeOrders
      .filter((c) => c.status === "approved")
      .reduce((sum, c) => sum + contingencyPortion(c), 0);
    const budgetFundedChange = budget.approvedChangeCents - contingencyFunded;

    const control = rehabControl({
      baselineCents: budget.baseline?.totalCents ?? 0,
      approvedChangeCents: budgetFundedChange,
      contingencyFundedCents: contingencyFunded,
      initialContingencyCents: budget.baseline?.contingencyCents ?? Number(project.contingencyCents ?? 0),
      currentWorkingCents: budget.currentWorkingCents,
      projectedFinalCents: projectedRehabCents,
      actualToDateCents: buckets.renovation,
      hasBaseline: budget.baseline !== null,
      projectionIncomplete: projection.incomplete,
    });

    const saleOutcome = sale
      ? saleResult(
          {
            saleDateISO: sale.saleDate,
            grossSalePriceCents: sale.grossSalePriceCents,
            commissionCents: sale.commissionCents,
            sellerClosingCents: sale.sellerClosingCents,
            concessionsCents: sale.concessionsCents,
            otherSellingCents: sale.otherSellingCents,
            costsRecordedInAccounting: sale.costsRecordedInAccounting,
          },
          buckets.selling,
        )
      : null;

    // Financing cost = what has been PAID (financing-bucket actuals) plus
    // what the loan terms say is still COMING (future interest and fees
    // from the pure ledger). Interest and fees only — lender principal is
    // funding, never a cost (spec §73.1H, test-pinned).
    const futureFinancingCents = this.financingProjector ? await this.financingProjector(projectId) : 0;
    const cost = projectCost({
      purchasePriceCents: project.purchasePriceCents ?? 0,
      acquisitionCostsCents: acquisitionExpensesCents(acquisitionLines) + buckets.acquisition,
      rehabCents: projectedRehabCents,
      rehabIsProjected: projection.incomplete,
      holdingCents: buckets.holding,
      financingCents: buckets.financing + futureFinancingCents,
      sellingCents: buckets.selling,
      otherCents: buckets.other,
    });

    // Before the sale the forecast uses the current Expected ARV; after it,
    // the real net proceeds. The two are never presented as the same thing.
    const isForecast = saleOutcome === null || saleOutcome.grossSalePriceCents === 0;
    const proceeds = isForecast ? (project.expectedArvCents ?? 0) : saleOutcome!.netProceedsCents;
    const outcome = returnMetrics({
      netProceedsCents: proceeds,
      totalProjectCostCents: cost.totalCents,
      purchaseDateISO: (project as { purchaseDate?: string | null }).purchaseDate ?? (await this.purchaseDate(projectId)),
      saleDateISO: sale?.saleDate ?? null,
      isForecast,
    });

    return {
      projectId,
      jobNumber: project.jobNumber,
      projectName: project.name,
      projectType: project.projectType === "new_construction" ? "new_construction" : "renovation",
      statusId: project.statusId,
      purchaseDate: await this.purchaseDate(projectId),
      purchasePriceCents: project.purchasePriceCents ?? 0,
      acquisitionCostsCents: acquisitionExpensesCents(acquisitionLines) + buckets.acquisition,
      bucketTotals: buckets,
      mileageCents: mileageRows.filter((m) => m.voidedAt === null).reduce((s, m) => s + m.amountCents, 0),
      rehab: control,
      projection,
      cost,
      sale: saleOutcome ? { ...saleOutcome, saleDate: sale!.saleDate, closedAt: sale!.closedAt } : null,
      renovationBasisDeltaCents,
      strategy: await this.strategyOf(projectId),
      financingOutlook: {
        actualToDateCents: buckets.financing,
        projectedFutureCents: futureFinancingCents,
        totalProjectedCents: buckets.financing + futureFinancingCents,
      },
      expectedArvCents: project.expectedArvCents,
      baselineExpectedArvCents: budget.baseline?.expectedArvCents ?? null,
      outcome,
      committedCostSeam: COMMITTED_COST_SEAM,
      warnings: await this.closeoutWarnings(projectId, reconciliationResult, budget, live),
    };
  }

  /**
   * Estimate lines that belong to no feature — a quick manual estimate is
   * still scope. Money linked directly to such a line is attributed to it
   * and removed from the "unassigned" figure, so nothing counts twice.
   */
  private async unlinkedScope(
    projectId: string,
    events: ActualCostEvent[],
    reconciliationResult: Awaited<ReturnType<ReconciliationService["forProject"]>>,
    categories?: Awaited<ReturnType<CategoryRepository["listAll"]>>,
  ): Promise<{ unlinkedLines: UnlinkedProposedLine[]; unassignedActualCents: Cents }> {
    const categoryList = categories ?? (await this.categories.listAll());
    const bucketOf = new Map(categoryList.map((c) => [c.id, c.defaultBucket]));
    const designId = (
      await this.db.select<{ active_design_id: string | null }>("SELECT active_design_id FROM projects WHERE id = ?", [projectId])
    )[0]?.active_design_id ?? null;
    const rows = await this.db.select<{ id: string; description: string; amount_cents: number; category_id: string | null; cost_type: string | null }>(
      `SELECT id, description, amount_cents, category_id, cost_type FROM proposed_cost_lines
        WHERE project_id = ? AND feature_id IS NULL AND archived_at IS NULL`,
      [projectId],
    );
    void designId;
    const actualByLine = new Map<string, Cents>();
    for (const event of events) {
      if (!event.linkedProposedLineId) continue;
      actualByLine.set(
        event.linkedProposedLineId,
        (actualByLine.get(event.linkedProposedLineId) ?? 0) + event.amountCents + event.taxCents,
      );
    }
    let claimed = 0;
    const unlinkedLines: UnlinkedProposedLine[] = rows.map((row) => {
      const actual = actualByLine.get(row.id) ?? 0;
      claimed += actual;
      return {
        lineId: row.id,
        description: row.description,
        categoryId: row.category_id,
        basis: (row.cost_type as UnlinkedProposedLine["basis"]) ?? null,
        proposedCents: Number(row.amount_cents ?? 0),
        actualCents: actual,
      };
    });
    // Only RENOVATION money belongs in the rehab projection: holding,
    // financing and selling costs are their own buckets in the project cost.
    const unassignedRenovation = events
      .filter((event) => {
        if (event.linkedProposedLineId || event.linkedFeatureId) return false;
        if(reconciliationResult.settlements.some(s=>s.eventId===event.id))return false;
        const bucket = event.bucketOverride ?? bucketOf.get(event.categoryId) ?? "other";
        return bucket === "renovation";
      })
      .reduce((sum, event) => sum + event.amountCents + event.taxCents, 0);
    void claimed;
    return {
      unlinkedLines,
      // No subtraction here: the filter above already excludes money linked
      // to a line or feature, so subtracting it again would silently drop
      // real spend (it did — found in Phase 12 acceptance).
      unassignedActualCents: unassignedRenovation,
    };
  }

  private async purchaseDate(projectId: string): Promise<string | null> {
    const rows = await this.db.select<{ purchase_date: string | null }>(
      "SELECT purchase_date FROM projects WHERE id = ?",
      [projectId],
    );
    return rows[0]?.purchase_date ?? null;
  }

  async setPurchaseDate(projectId: string, date: string | null): Promise<void> {
    await this.db.execute("UPDATE projects SET purchase_date = ?, updated_at = ? WHERE id = ?", [
      date,
      new Date().toISOString(),
      projectId,
    ]);
  }

  /** The same projected truth, rolled up by room, category, feature or basis. */
  async rollups(
    projectId: string,
    dimension: "room" | "category" | "feature" | "basis",
  ): Promise<RollupRow[]> {
    const [reconciliationResult, categories] = await Promise.all([
      this.reconciliation.forProject(projectId),
      this.categories.listAll(),
    ]);
    const events = (await this.reconciliation.eventsForProject(projectId)).filter((e) => e.voidedAt === null);
    const unlinked = await this.unlinkedScope(projectId, events, reconciliationResult);
    return rollup(projectRehab(reconciliationResult, unlinked), dimension, {
      categories: new Map(categories.map((c) => [c.id, c.displayName])),
    });
  }

  /* ------------------------------------------------------------------ */
  /* Closeout readiness                                                  */
  /* ------------------------------------------------------------------ */

  /**
   * What is unresolved. These WARN — closing is never blocked, because a
   * house sells whether or not the paperwork is tidy.
   */
  private async closeoutWarnings(
    projectId: string,
    reconciliationResult: Awaited<ReturnType<ReconciliationService["forProject"]>>,
    budget: Awaited<ReturnType<BaselineService["budget"]>>,
    events: ActualCostEvent[],
  ): Promise<CloseoutWarning[]> {
    const warnings: CloseoutWarning[] = [];
    const incomplete = reconciliationResult.totals.incompleteFeatureCount;
    if (incomplete > 0) {
      warnings.push({
        id: "incomplete-bases",
        label: `${incomplete} item${incomplete === 1 ? "" : "s"} not fully reconciled`,
        detail: "Their final cost is still a projection, so the variance is not final either.",
        where: "reconciliation",
        count: incomplete,
      });
    }
    if (budget.draftChangeOrderCount > 0) {
      warnings.push({
        id: "draft-change-orders",
        label: `${budget.draftChangeOrderCount} draft change order${budget.draftChangeOrderCount === 1 ? "" : "s"}`,
        detail: "Drafts authorize nothing — approve or cancel them so the authorized budget is the real one.",
        where: "scope",
        count: budget.draftChangeOrderCount,
      });
    }
    if (budget.baseline !== null && budget.unapprovedDeltaCents !== 0) {
      warnings.push({
        id: "unapproved-planning",
        label: "Planning changes are not authorized",
        detail: "The working estimate differs from the authorized budget; authorize or revert the difference.",
        where: "scope",
        count: 1,
      });
    }
    const undocumented = await this.undocumentedCount(projectId, events);
    if (undocumented > 0) {
      warnings.push({
        id: "missing-documents",
        label: `${undocumented} record${undocumented === 1 ? "" : "s"} without a source document`,
        detail: "Receipts and invoices are what make the accounting records checkable later.",
        where: "accounting",
        count: undocumented,
      });
    }
    const pendingReceipts = await this.pendingReceiptCount(projectId);
    if (pendingReceipts > 0) {
      warnings.push({
        id: "receipts-awaiting-review",
        label: `${pendingReceipts} receipt${pendingReceipts === 1 ? "" : "s"} awaiting review`,
        detail: "They are proposals until you confirm them — nothing is in the accounts yet.",
        where: "receipts",
        count: pendingReceipts,
      });
    }
    try {
      const procurement = await this.procurement.forProject(projectId);
      const outstanding = procurement.filter(
        (row) => row.fulfillment.status === "need-to-purchase" || row.fulfillment.status === "partially-purchased",
      ).length;
      if (outstanding > 0) {
        warnings.push({
          id: "outstanding-procurement",
          label: `${outstanding} material requirement${outstanding === 1 ? "" : "s"} still outstanding`,
          detail: "Either they are still to buy, or the purchase was never linked to the requirement.",
          where: "materials",
          count: outstanding,
        });
      }
    } catch {
      /* procurement is optional context; never block the dashboard */
    }
    return warnings;
  }

  /** Actual records with no receipt/invoice attached. */
  private async undocumentedCount(projectId: string, events: ActualCostEvent[]): Promise<number> {
    const expenseIds = events.filter((e) => e.source === "expense").map((e) => e.id);
    let undocumented = 0;
    if (expenseIds.length > 0) {
      const placeholders = expenseIds.map(() => "?").join(",");
      const documented = await this.db.select<{ expense_id: string }>(
        `SELECT DISTINCT expense_id FROM actual_expense_attachments WHERE expense_id IN (${placeholders})`,
        expenseIds,
      );
      const set = new Set(documented.map((d) => d.expense_id));
      undocumented += expenseIds.filter((id) => !set.has(id)).length;
    }
    const purchases = await this.db.select<{ id: string; n: number }>(
      `SELECT p.id, (SELECT COUNT(*) FROM purchase_attachments pa WHERE pa.purchase_id = p.id) AS n
         FROM purchases p
        WHERE p.id IN (SELECT l.purchase_id FROM purchase_lines l
                        JOIN purchase_allocations a ON a.line_id = l.id WHERE a.project_id = ?)`,
      [projectId],
    );
    undocumented += purchases.filter((p) => Number(p.n) === 0).length;
    return undocumented;
  }

  private async pendingReceiptCount(projectId: string): Promise<number> {
    const rows = await this.db.select<{ n: number }>(
      "SELECT COUNT(*) AS n FROM document_extractions WHERE project_id = ? AND status IN ('needs-review','ready')",
      [projectId],
    );
    return Number(rows[0]?.n ?? 0);
  }

  /* ------------------------------------------------------------------ */
  /* Sale & closeout                                                     */
  /* ------------------------------------------------------------------ */

  async getSale(projectId: string): Promise<ProjectSale | null> {
    const rows = await this.db.select<SaleRow>("SELECT * FROM project_sales WHERE project_id = ?", [projectId]);
    return rows.length === 0 ? null : toSale(rows[0]);
  }

  async saveSale(projectId: string, input: ProjectSaleInput): Promise<ProjectSale> {
    const problems = validateSaleInput(input);
    if (problems.length > 0) throw new ValidationError(problems);
    const now = new Date().toISOString();
    const existing = await this.getSale(projectId);
    if (existing) {
      await this.db.execute(
        `UPDATE project_sales SET sale_date = ?, gross_sale_price_cents = ?, commission_cents = ?,
           seller_closing_cents = ?, concessions_cents = ?, other_selling_cents = ?,
           costs_recorded_in_accounting = ?, notes = ?, updated_at = ? WHERE project_id = ?`,
        [
          input.saleDate, input.grossSalePriceCents, input.commissionCents, input.sellerClosingCents,
          input.concessionsCents, input.otherSellingCents, input.costsRecordedInAccounting ? 1 : 0,
          input.notes, now, projectId,
        ],
      );
    } else {
      await this.db.execute(
        `INSERT INTO project_sales (project_id, sale_date, gross_sale_price_cents, commission_cents,
           seller_closing_cents, concessions_cents, other_selling_cents, costs_recorded_in_accounting,
           notes, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          projectId, input.saleDate, input.grossSalePriceCents, input.commissionCents,
          input.sellerClosingCents, input.concessionsCents, input.otherSellingCents,
          input.costsRecordedInAccounting ? 1 : 0, input.notes, now, now,
        ],
      );
    }
    return (await this.getSale(projectId))!;
  }

  /**
   * Mark the project sold/closed. Warnings are surfaced, never blocking —
   * and closing is reversible, because a correction must always be possible.
   */
  async closeProject(projectId: string, options: { statusId?: string; closedBy?: string | null } = {}): Promise<void> {
    const sale = await this.getSale(projectId);
    if (!sale || sale.grossSalePriceCents === 0) {
      throw new ValidationError(["Record the sale price before closing the project."]);
    }
    const now = new Date().toISOString();
    await this.db.transaction(async () => {
      await this.db.execute(
        "UPDATE project_sales SET closed_at = ?, closed_by = ?, reopened_at = NULL, updated_at = ? WHERE project_id = ?",
        [now, options.closedBy ?? null, now, projectId],
      );
      await this.db.execute("UPDATE projects SET status_id = ?, updated_at = ? WHERE id = ?", [
        options.statusId ?? "sold",
        now,
        projectId,
      ]);
    });
  }

  /** Reopen for a correction; the closed timestamp is kept as history. */
  async reopenProject(projectId: string, statusId = "active"): Promise<void> {
    const now = new Date().toISOString();
    await this.db.transaction(async () => {
      await this.db.execute(
        "UPDATE project_sales SET reopened_at = ?, closed_at = NULL, updated_at = ? WHERE project_id = ?",
        [now, now, projectId],
      );
      await this.db.execute("UPDATE projects SET status_id = ?, updated_at = ? WHERE id = ?", [statusId, now, projectId]);
    });
  }

  async isClosed(projectId: string): Promise<boolean> {
    const sale = await this.getSale(projectId);
    return sale?.closedAt !== null && sale?.closedAt !== undefined;
  }

  /* ------------------------------------------------------------------ */
  /* Accounting-ready records                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Where every dollar went: bucket → category → transaction, with the
   * source-document status of each record. Facts only — the app organizes
   * and exports, it never states tax treatment.
   */
  async accountingRecords(projectId: string): Promise<AccountingBucketGroup[]> {
    const [events, categories, mileageRows] = await Promise.all([
      this.reconciliation.eventsForProject(projectId),
      this.categories.listAll(),
      this.mileage.listByProject(projectId),
    ]);
    const categoryById = new Map(categories.map((c) => [c.id, c]));
    const documented = await this.documentedIds(projectId);
    const provenance = await this.reconciliation.provenance(
      events.filter((e) => e.voidedAt === null).map((e) => e.id),
      projectId,
    );
    const byEvent = new Map(provenance.map((p) => [p.eventId, p]));
    const mileageById = new Map(mileageRows.map((m) => [m.id, m]));

    const groups = new Map<FinancialBucket, AccountingBucketGroup>();
    for (const bucket of BUCKET_ORDER) {
      groups.set(bucket, { bucket, totalCents: 0, categories: [] });
    }
    for (const event of events) {
      if (event.voidedAt !== null) continue;
      const category = categoryById.get(event.categoryId);
      const bucket = event.bucketOverride ?? category?.defaultBucket ?? "other";
      const group = groups.get(bucket)!;
      const categoryKey = event.categoryId || "uncategorized";
      let categoryGroup = group.categories.find((c) => c.categoryId === categoryKey);
      if (!categoryGroup) {
        categoryGroup = {
          categoryId: categoryKey,
          categoryName: category?.displayName ?? "Uncategorized",
          totalCents: 0,
          rows: [],
        };
        group.categories.push(categoryGroup);
      }
      const record = byEvent.get(event.id);
      const mileageEntry = event.source === "mileage" ? mileageById.get(event.id) : undefined;
      const row: AccountingRow = {
        eventId: event.id,
        date: event.date,
        vendor: record?.vendor ?? "",
        description: event.description,
        amountCents: event.amountCents,
        taxCents: event.taxCents,
        totalCents: expenseTotalCents(event),
        source: event.source,
        costBasis: event.costType,
        categoryName: categoryGroup.categoryName,
        bucket,
        documentStatus: documentStatusFor(event, documented),
        purchaseId: record?.purchaseId ?? null,
        expenseId: record?.expenseId ?? null,
        isReturn: record?.isReturn ?? false,
        mileage: mileageEntry
          ? {
              milesX10: mileageEntry.milesX10,
              rateCentsPerMile: mileageEntry.rateCentsPerMile,
              startLocation: mileageEntry.startLocation,
              destination: mileageEntry.destination,
            }
          : null,
      };
      categoryGroup.rows.push(row);
      categoryGroup.totalCents += row.totalCents;
      group.totalCents += row.totalCents;
    }
    for (const group of groups.values()) {
      group.categories.sort((a, b) => b.totalCents - a.totalCents);
      for (const category of group.categories) category.rows.sort((a, b) => a.date.localeCompare(b.date));
    }
    return [...groups.values()].filter((g) => g.categories.length > 0);
  }

  private async documentedIds(projectId: string): Promise<{ expenses: Set<string>; purchases: Set<string> }> {
    const expenses = await this.db.select<{ expense_id: string }>(
      `SELECT DISTINCT expense_id FROM actual_expense_attachments
        WHERE expense_id IN (SELECT id FROM actual_expenses WHERE project_id = ?)`,
      [projectId],
    );
    const purchases = await this.db.select<{ purchase_id: string }>(
      `SELECT DISTINCT purchase_id FROM purchase_attachments
        WHERE purchase_id IN (SELECT l.purchase_id FROM purchase_lines l
                               JOIN purchase_allocations a ON a.line_id = l.id WHERE a.project_id = ?)`,
      [projectId],
    );
    return {
      expenses: new Set(expenses.map((e) => e.expense_id)),
      purchases: new Set(purchases.map((p) => p.purchase_id)),
    };
  }
}

function contingencyPortion(order: { status: string; netDeltaCents: Cents } & Partial<{ fundingSource: string; contingencyCents: Cents }>): Cents {
  if (order.status !== "approved") return 0;
  if (order.fundingSource === "contingency") return order.netDeltaCents;
  return order.contingencyCents ?? 0;
}

function documentStatusFor(
  event: ActualCostEvent,
  documented: { expenses: Set<string>; purchases: Set<string> },
): AccountingRow["documentStatus"] {
  if (event.source === "mileage") return "not-required";
  if (event.source === "expense") return documented.expenses.has(event.id) ? "attached" : "missing";
  return "purchase";
}

export interface AccountingRow {
  eventId: string;
  date: string;
  vendor: string;
  description: string;
  amountCents: Cents;
  taxCents: Cents;
  totalCents: Cents;
  source: ActualCostEvent["source"];
  costBasis: ActualCostEvent["costType"];
  categoryName: string;
  bucket: FinancialBucket;
  /** attached | missing | purchase (checked on the purchase) | not-required */
  documentStatus: "attached" | "missing" | "purchase" | "not-required";
  purchaseId: string | null;
  expenseId: string | null;
  isReturn: boolean;
  mileage: { milesX10: number; rateCentsPerMile: Cents; startLocation: string; destination: string } | null;
}

export interface AccountingCategoryGroup {
  categoryId: string;
  categoryName: string;
  totalCents: Cents;
  rows: AccountingRow[];
}

export interface AccountingBucketGroup {
  bucket: FinancialBucket;
  totalCents: Cents;
  categories: AccountingCategoryGroup[];
}

interface SaleRow {
  project_id: string;
  sale_date: string | null;
  gross_sale_price_cents: number;
  commission_cents: number;
  seller_closing_cents: number;
  concessions_cents: number;
  other_selling_cents: number;
  costs_recorded_in_accounting: number;
  notes: string;
  closed_at: string | null;
  closed_by: string | null;
  reopened_at: string | null;
  created_at: string;
  updated_at: string;
}

function toSale(row: SaleRow): ProjectSale {
  return {
    projectId: row.project_id,
    saleDate: row.sale_date,
    grossSalePriceCents: Number(row.gross_sale_price_cents ?? 0),
    commissionCents: Number(row.commission_cents ?? 0),
    sellerClosingCents: Number(row.seller_closing_cents ?? 0),
    concessionsCents: Number(row.concessions_cents ?? 0),
    otherSellingCents: Number(row.other_selling_cents ?? 0),
    costsRecordedInAccounting: Number(row.costs_recorded_in_accounting) === 1,
    notes: row.notes ?? "",
    closedAt: row.closed_at,
    closedBy: row.closed_by,
    reopenedAt: row.reopened_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}
